// Small presentational badges shared across the Service Desk (portal + agent).
import { useTranslation } from "react-i18next";
import {
  SLA_META,
  PRIORITY_META,
  AI_AGENT_META,
  type SlaState,
  type TicketPriority,
  type AiAgentState,
} from "../services/serviceDeskService";
import { REWORK_META } from "../services/ticketClosure";

export function SlaBadge({ state }: { state?: SlaState }) {
  const meta = SLA_META[state || "unknown"];
  const isBreached = state === "breached";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isBreached ? "ring-1 ring-red-300" : ""}`}
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      {isBreached ? <span aria-hidden="true">⚠</span> : <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />}
      SLA: {meta.label}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: `${meta.color}1a` }}
    >
      {meta.label}
    </span>
  );
}

/**
 * Estado del agente IA sobre el ticket. `idle` no se pinta: un ticket sin
 * trabajar es el caso normal y llenar el tablero de chips grises no informa.
 */
export function AiAgentBadge({ state }: { state?: AiAgentState }) {
  // `sandbox` (ticket de ejemplo) tampoco: ese aviso lo da `SampleChip`.
  if (!state || state === "idle" || state === "sandbox") return null;
  const meta = AI_AGENT_META[state];
  if (!meta) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg }}
      title={`Agente IA: ${meta.label}`}
    >
      <span aria-hidden>&#9881;</span>
      IA: {meta.label}
    </span>
  );
}

/**
 * "Rehecho ×N" (CC-CONSOLE #1337): how many times the ticket came back from
 * "Resuelto" because it was not solved. Not rendered at 0 — the normal case.
 */
export function ReworkChip({ count }: { count?: number | null }) {
  const { t } = useTranslation("serviceDesk");
  if (!count || count < 1) return null;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: REWORK_META.color, backgroundColor: `${REWORK_META.color}1a` }}
      title={t("closure.reworkChipTitle", "Veces que se regresó a «Rehacer» porque no quedó resuelto")}
    >
      {/* `n`, not `count`: i18next treats `count` as a plural and would look for _one/_other. */}
      {t("closure.reworkChip", "Rehecho ×{{n}}", { n: count })}
    </span>
  );
}

/** ¿Ticket de ejemplo del Modo Sandbox? */
export function isSampleTicket(ticket?: { sandbox?: boolean; demoSeed?: boolean } | null): boolean {
  return Boolean(ticket && (ticket.sandbox === true || ticket.demoSeed === true));
}

/**
 * Chip «Ejemplo» de un ticket del Modo Sandbox (CC-CONSOLE#1598): un admin
 * reseller que trabaja la mesa real con una compañía de prueba seleccionada
 * distingue de un vistazo lo que no es de un cliente.
 */
export function SampleChip({ ticket }: { ticket?: { sandbox?: boolean; demoSeed?: boolean } | null }) {
  const { t } = useTranslation("serviceDesk");
  if (!isSampleTicket(ticket)) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-dashed border-amber-400 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-300"
      title={t("sample.title")}
    >
      <i className="ri-flask-line" aria-hidden="true" />
      {t("sample.label")}
    </span>
  );
}

/** Iniciales de un nombre («Mariana Robles» → «MR»). */
export function initialsOf(name?: string | null): string {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] || "" : "")).toUpperCase();
}

const AVATAR_COLORS = ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#6366F1"];

/** Avatar de iniciales de un agente (sin foto: el color sale del nombre). */
export function AgentAvatar({ name, size = 22 }: { name?: string | null; size?: number }) {
  const label = String(name || "");
  let h = 0;
  for (let i = 0; i < label.length; i += 1) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[h % AVATAR_COLORS.length];
  return (
    <span
      className="inline-flex flex-shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), backgroundColor: color }}
      title={label}
      aria-hidden={!label}
    >
      {initialsOf(label)}
    </span>
  );
}

const CHANNEL_ICON: Record<string, string> = {
  whatsapp: "ri-whatsapp-line",
  email: "ri-mail-line",
  portal: "ri-global-line",
  phone: "ri-phone-line",
};

/** Canal por el que llegó el ticket (WhatsApp, correo, portal, teléfono). */
export function ChannelChip({ channel }: { channel?: string | null }) {
  const { t } = useTranslation("serviceDesk");
  if (!channel || !CHANNEL_ICON[channel]) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-subtle px-2 py-0.5 text-xs font-medium text-content-secondary"
      title={t("channels.label")}
    >
      <i className={CHANNEL_ICON[channel]} aria-hidden="true" />
      {t(`channels.${channel}`)}
    </span>
  );
}

export function StatusBadge({ label, color }: { label: string; color?: string }) {
  const c = color || "#6b7280";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ color: c, backgroundColor: `${c}1a` }}
    >
      {label}
    </span>
  );
}

export function LabelChip({ text, color }: { text: string; color?: string }) {
  const c = color || "#64748b";
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium"
      style={{ color: c, backgroundColor: `${c}1a` }}
    >
      {text}
    </span>
  );
}
